/* eslint-disable no-unused-vars */
/**
 * PRSAnalytics Component
 *
 * This component now supports dynamic column filtering based on backend-driven configuration.
 * The API response includes a `queryParams.searchable` array that defines which columns
 * should have search/filter functionality enabled.
 *
 * Features:
 * - Dynamic column search based on API response
 * - Session storage for filter state persistence
 * - Backend-driven searchable field configuration
 * - Automatic filter initialization based on searchable fields
 */
import React, { useState, useEffect, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Spin,
  message,
  Table,
  DatePicker,
  Button,
  Space,
  Statistic,
  Input,
} from "antd";
import {
  ReloadOutlined,
  DownloadOutlined,
  AppstoreOutlined,
  SearchOutlined,
  ClearOutlined,
  FilterOutlined,
  UpOutlined,
  DownOutlined,
} from "@ant-design/icons";
import moment from "moment";
import API from "../../api";
import urls from "../../api/urls";
import "./style.scss";
import { dateFormat_dmy } from "../../utils";
import useCustomNavigate from "../../components/customNavigationHook";

const { RangePicker } = DatePicker;

// Function to get fresh initial state with non-mutated moment objects
const getInitialState = () => ({
  pagination: { current: 1, pageSize: 10, total: 0 },
  dateRange: [moment().startOf("day"), moment().endOf("day")],
  columnFilters: {
    awbNumber: "", // Keep AWB Number filter for backwards compatibility
  },
  searchableFields: [],
});

// Function to initialize column filters based on data keys
const initializeColumnFilters = (dataSource, searchableFieldsArray = []) => {
  if (!dataSource || dataSource.length === 0) {
    // Return filters for searchable fields even if no data
    const filters = {};
    searchableFieldsArray.forEach((field) => {
      filters[field] = "";
    });
    return Object.keys(filters).length > 0 ? filters : { awbNumber: "" };
  }

  const sampleRecord = dataSource[0];
  const keys = Object.keys(sampleRecord);
  const filters = {};

  // Initialize filters only for searchable fields if provided
  if (searchableFieldsArray.length > 0) {
    searchableFieldsArray.forEach((field) => {
      if (keys.includes(field)) {
        filters[field] = "";
      }
    });
  } else {
    // Fallback to all keys if no searchable fields specified
    keys.forEach((key) => {
      filters[key] = "";
    });
  }

  return filters;
};

// Use the imported API endpoints
const {
  PRS_ANALYTICS_ENDPOINT,
  PRS_ORDERS_ENDPOINT,
  GET_PRS_DATA_V2,
  PRS_STATUS_COUNTS_ENDPOINT,
  FETCH_TOTAL_ORDERS_COUNT,
} = urls;

function PRSAnalytics() {
  const [form] = Form.useForm();
  const navigate = useCustomNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const initialState = getInitialState();
  const [pagination, setPagination] = useState(initialState.pagination);
  const [dateRange, setDateRange] = useState(initialState.dateRange);
  const [columnFilters, setColumnFilters] = useState(
    initialState.columnFilters
  );
  const [totalOrdersManifested, setTotalOrdersManifested] = useState(0);
  const [totalOrdersInPRS, setTotalOrdersInPRS] = useState(0);
  const [totalOrdersCount, setTotalOrdersCount] = useState(0);

  // Single dynamic state for all PRS status counts
  const [statusCounts, setStatusCounts] = useState([]);

  // State to store searchable fields from API response
  const [searchableFields, setSearchableFields] = useState([]);

  // State for collapsible cards section
  const [cardsCollapsed, setCardsCollapsed] = useState(true);

  // Debounce ref for search with timeout tracking
  const debounceRef = useRef({});

  // Session storage key
  const SESSION_STORAGE_KEY = "prs_analytics_state";

  // Helper functions for session storage
  const saveStateToSession = () => {
    const state = {
      columnFilters,
      searchableFields,
      dateRange: dateRange.map((date) => date.toISOString()),
      pagination: {
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
      },
    };
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state));
  };

  const getStateFromSession = () => {
    try {
      const saved = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (!saved) return null;
      const state = JSON.parse(saved);

      // Convert date strings back to moment objects
      if (state.dateRange && state.dateRange.length === 2) {
        state.dateRange = [
          moment(state.dateRange[0]),
          moment(state.dateRange[1]),
        ];
        console.log("Restored date range from session:", {
          restored: state.dateRange.map((d) => d.format("DD-MM-YYYY")),
          startTime: state.dateRange[0].startOf("day").toISOString(),
          endTime: state.dateRange[1].endOf("day").toISOString(),
        });
      }

      return state;
    } catch (error) {
      return null;
    }
  };

  const clearStateFromSession = () => {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  };

  // Update filter state without triggering API call
  const handleColumnFilter = (value, field) => {
    // Create updated filters object immediately
    const updatedFilters = {
      ...columnFilters,
      [field]: value,
    };

    // Update the filter state immediately for UI responsiveness
    setColumnFilters(updatedFilters);

    // Save state to session storage
    setTimeout(() => saveStateToSession(), 100);
  };

  // Trigger search with current filters
  const handleSearch = (field = null, value = null) => {
    // If specific field and value provided, update that filter first
    let filtersToUse = columnFilters;
    if (field && value !== null) {
      filtersToUse = {
        ...columnFilters,
        [field]: value,
      };
      setColumnFilters(filtersToUse);
    }

    // Reset pagination to first page when searching
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));

    // Clear existing timeouts
    Object.values(debounceRef.current).forEach((timeout) => {
      if (timeout) clearTimeout(timeout);
    });
    debounceRef.current = {};

    // Call API with current filters
    fetchPRSData(1, pagination.pageSize, ["ECOMM"], filtersToUse);
  };

  const handleClearFilters = () => {
    // Clear all existing debounce timeouts
    Object.values(debounceRef.current).forEach((timeout) => {
      if (timeout) clearTimeout(timeout);
    });
    debounceRef.current = {};

    // Clear all column filters - reset based on current data structure and searchable fields
    const clearedFilters =
      data.length > 0
        ? initializeColumnFilters(data, searchableFields)
        : getInitialState().columnFilters;

    setColumnFilters(clearedFilters);

    // Reset pagination to first page
    setPagination({
      ...pagination,
      current: 1,
    });

    // Clear session storage since filters are reset
    clearStateFromSession();

    // Fetch data immediately without debounce (filters will be empty after reset)
    fetchPRSData(1, pagination.pageSize, ["ECOMM"], clearedFilters);
  };

  const handleTableChange = (newPagination) => {
    if (newPagination.current) {
      const updatedPagination = {
        current: newPagination.current,
        pageSize: newPagination.pageSize || 10,
        total: pagination.total,
      };

      setPagination(updatedPagination);

      // Save state to session storage
      setTimeout(() => saveStateToSession(), 100);

      fetchPRSData(newPagination.current, newPagination.pageSize || 10);
    }
  };

  // Common function to fetch PRS data with filters
  const fetchPRSData = async (
    page = 1,
    pageSize = 10,
    type = ["ECOMM"],
    filters = null
  ) => {
    if (
      !dateRange[0]?.startOf("day").toISOString() ||
      !dateRange[1]?.endOf("day").toISOString()
    ) {
      return;
    }
    try {
      const queryParams = {
        startTime: dateRange[0].startOf("day").toISOString(),
        endTime: dateRange[1].endOf("day").toISOString(),
        // type: type,
        pageNumber: page,
        pageSize: pageSize,
      };

      // Use provided filters or fall back to current columnFilters state
      const filtersToUse = filters || columnFilters;

      // Add column filters to query params - pass the column key with its value
      Object.entries(filtersToUse).forEach(([columnKey, columnValue]) => {
        if (columnValue && columnValue.trim() !== "") {
          queryParams[columnKey] = columnValue.trim();
        }
      });

      const queryString = new URLSearchParams(queryParams).toString();

      const response = await API.Api(
        GET_PRS_DATA_V2 + "?" + queryString
      ).getCustomHeader({
        kid: "1",
        authtype: "jwt",
      });

      if (response?.response?.data?.status === 200) {
        // Always clear existing data and set new data to prevent showing stale data
        const newData = response.response.data.data || [];
        const totalCount = response.response.data.totalCount || 0;

        // Extract searchable fields from API response
        const apiSearchableFields =
          response.response.data.queryParams?.searchable || [];
        setSearchableFields(apiSearchableFields);

        console.log("Searchable fields from API:", apiSearchableFields);

        setData(newData);
        setPagination({
          current: page,
          pageSize,
          total: totalCount,
        });

        // Initialize column filters based on the new data structure and searchable fields
        if (newData.length > 0 && Object.keys(columnFilters).length <= 1) {
          const newFilters = initializeColumnFilters(
            newData,
            apiSearchableFields
          );
          // Preserve existing filters if they exist
          Object.keys(columnFilters).forEach((key) => {
            if (columnFilters[key]) {
              newFilters[key] = columnFilters[key];
            }
          });
          setColumnFilters(newFilters);
        }

        // Save state to session storage
        setTimeout(() => saveStateToSession(), 100);
      } else {
        // Clear data on error to prevent showing stale data
        setData([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        message.error(
          response?.response?.data?.message || "Failed to fetch ECOMM analytics"
        );
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Error fetching ECOMM analytics"
      );
    }
  };

  const fetchTotalOrdersCount = async () => {
    if (
      !dateRange[0]?.startOf("day").toISOString() ||
      !dateRange[1]?.endOf("day").toISOString()
    ) {
      message.error("Please select date range");
      return;
    }

    try {
      const startTime = dateRange[0].startOf("day").toISOString();
      const endTime = dateRange[1].endOf("day").toISOString();

      const apiUrl = `${FETCH_TOTAL_ORDERS_COUNT}?startTime=${encodeURIComponent(
        startTime
      )}&endTime=${encodeURIComponent(endTime)}`;

      const response = await API.Api(apiUrl).get();

      if (response?.response?.data?.data?.totalCount) {
        setTotalOrdersCount(response.response.data.data.totalCount || 0);
      }
    } catch (error) {
      console.error("Error fetching total orders count:", error);
      message.error(error?.message || "Error fetching total orders count");
    }
  };

  const fetchPRSAnalytics = async (
    page = 1,
    pageSize = 10,
    type = ["ECOMM"]
  ) => {
    if (
      !dateRange[0]?.startOf("day").toISOString() ||
      !dateRange[1]?.endOf("day").toISOString()
    ) {
      message.error("Please select date range");
      return;
    }

    try {
      const response = await API.Api(PRS_ANALYTICS_ENDPOINT).post({
        searchFilter: {
          startTime: dateRange[0].startOf("day").toISOString(),
          endTime: dateRange[1].endOf("day").toISOString(),
          excludeOrderStatus: ["NEW", "IN_PROCESS", "CANCELED", "ERROR_ORDER"],
          type: type,
        },
        projection: {
          awbNumber: 1,
          orderStatus: 1,
          prsNumber: 1,
          pickupPincode: 1,
          sellerName: 1,
          deliveryPartner: 1,
          pickupAddress: 1,
          city: 1,
          state: 1,
        },
        page,
        pageSize,
      });

      if (response?.response?.data?.status === 200) {
        setTotalOrdersManifested(response.response.data.totalCount || 0);
      } else {
        message.error(response?.statusText || "Failed to fetch PRS analytics");
      }
    } catch (error) {
      console.error("Error fetching PRS analytics:", error);
      message.error("Error fetching PRS analytics");
    }
  };

  const fetchTotalOrdersInPRS = async () => {
    if (!dateRange[0] || !dateRange[1]) {
      return;
    }

    try {
      const queryParams = new URLSearchParams({
        startTime: dateRange[0].startOf("day").toISOString(),
        endTime: dateRange[1].endOf("day").toISOString(),
        type: ["ECOMM"],
      }).toString();

      const result = await API.Api(
        PRS_ORDERS_ENDPOINT + "?" + queryParams
      ).getCustomHeader({
        kid: "1",
        authtype: "jwt",
      });

      if (result?.response?.status === 200) {
        setTotalOrdersInPRS(result?.response?.data?.data || 0);
      } else {
        message.error("Failed to fetch total orders in PRS");
      }
    } catch (error) {
      message.error("Error fetching total orders in PRS");
    }
  };

  // Fetch all PRS Status Counts
  const fetchAllStatusCounts = async () => {
    if (!dateRange[0] || !dateRange[1]) {
      return;
    }

    try {
      const queryParams = new URLSearchParams({
        startTime: dateRange[0].startOf("day").toISOString(),
        endTime: dateRange[1].endOf("day").toISOString(),
      }).toString();

      const result = await API.Api(
        PRS_STATUS_COUNTS_ENDPOINT + "?" + queryParams
      ).getCustomHeader({
        kid: "1",
        authtype: "jwt",
      });

      if (result?.response?.status === 200 && result?.response?.data?.data) {
        const statusData = result.response.data.data;
        setStatusCounts(statusData);
      } else {
        message.error(
          result?.response?.data?.message || "Failed to fetch PRS status counts"
        );
        setStatusCounts([]);
      }
    } catch (error) {
      console.error("Error fetching PRS status counts:", error);
      message.error(
        error?.response?.data?.message || "Error fetching PRS status counts"
      );
    }
  };

  const handleExport = async () => {
    if (!dateRange[0] || !dateRange[1]) {
      message.error("Please select date range");
      return;
    }

    if (apiCallInProgress.current) {
      message.warning("Please wait for current operation to complete");
      return;
    }

    setLoading(true);
    try {
      const queryParams = {
        startTime: dateRange[0].startOf("day").toISOString(),
        endTime: dateRange[1].endOf("day").toISOString(),
        // type: ["ECOMM"],
        downloadCsv: "1",
      };

      // Add column filters to export query params - pass the column key with its value
      Object.entries(columnFilters).forEach(([columnKey, columnValue]) => {
        if (columnValue && columnValue.trim() !== "") {
          queryParams[columnKey] = columnValue.trim();
        }
      });

      const queryString = new URLSearchParams(queryParams).toString();

      const result = await API.Api(
        GET_PRS_DATA_V2 + "?" + queryString
      ).getCustomHeader({
        kid: "1",
        authtype: "jwt",
      });

      if (result?.response?.status === 200 && result?.response?.data) {
        const url = window.URL.createObjectURL(
          new Blob([result?.response?.data])
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `PRS_Analytics_${moment().format("YYYY-MM-DD")}.csv`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        message.success("Export successful");
      } else {
        message.error("Failed to export data");
      }
    } catch (error) {
      message.error("Error exporting data");
    } finally {
      setLoading(false);
    }
  };

  // Flag to prevent multiple simultaneous API calls
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const apiCallInProgress = useRef(false);

  // Function to handle all API calls
  const fetchAllData = async (
    page = 1,
    pageSize = 10,
    filters = null,
    skipDateCheck = false
  ) => {
    // Prevent multiple simultaneous calls
    if (apiCallInProgress.current) {
      console.log("API call already in progress, skipping duplicate call");
      return;
    }

    if (!skipDateCheck && (!dateRange[0] || !dateRange[1])) {
      console.log("Date range not available, skipping API call");
      return;
    }

    // Use current filters if none provided
    const filtersToUse = filters || columnFilters;
    const currentPage = page || pagination.current || 1;
    const currentPageSize = pageSize || pagination.pageSize || 10;

    console.log("Making API calls with params:", {
      page: currentPage,
      pageSize: currentPageSize,
      filters: filtersToUse,
      dateRange: dateRange.map((d) => d?.format?.("YYYY-MM-DD") || d),
      startTimeISO: dateRange[0]?.startOf("day").toISOString(),
      endTimeISO: dateRange[1]?.endOf("day").toISOString(),
    });

    apiCallInProgress.current = true;
    setLoading(true);

    try {
      // Call all APIs in parallel for better performance
      await Promise.all([
        fetchPRSData(currentPage, currentPageSize, ["ECOMM"], filtersToUse),
        // fetchPRSAnalytics(),
        // fetchTotalOrdersCount(),
        // fetchTotalOrdersInPRS(),
        // fetchAllStatusCounts(),
      ]);
    } catch (error) {
      // Errors are handled by individual API functions
    } finally {
      apiCallInProgress.current = false;
      setLoading(false);
      setIsInitialLoading(false);
    }
  };

  // Initial data loading and state restoration
  useEffect(() => {
    const savedState = getStateFromSession();

    if (savedState) {
      console.log("Restoring saved state:", savedState);
      // Restore saved state
      const freshState = getInitialState();
      setColumnFilters(savedState.columnFilters || freshState.columnFilters);
      setSearchableFields(savedState.searchableFields || []);
      setDateRange(savedState.dateRange || freshState.dateRange);
      setPagination(savedState.pagination || freshState.pagination);

      // Use setTimeout to ensure state is set before API call
      setTimeout(() => {
        fetchAllData(
          savedState.pagination?.current || 1,
          savedState.pagination?.pageSize || 10,
          savedState.columnFilters,
          false // Don't skip date check since we just restored dateRange
        );
      }, 100);
    } else {
      console.log("No saved state, using defaults");
      // No saved state, use defaults - skip date check for initial load
      const freshState = getInitialState();
      setSearchableFields(freshState.searchableFields);
      fetchAllData(1, 10, freshState.columnFilters, true);
    }
  }, []);

  // Handle date range changes
  useEffect(() => {
    // Skip if this is the initial loading or if date range is from restoration
    if (isInitialLoading || apiCallInProgress.current) {
      return;
    }

    if (dateRange[0] && dateRange[1]) {
      console.log("Date range changed, fetching data");
      const currentPage = pagination.current || 1;
      const currentPageSize = pagination.pageSize || 10;

      fetchAllData(currentPage, currentPageSize, columnFilters);
    }
  }, [dateRange, isInitialLoading]);

  // Cleanup debounce timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear all pending debounce timeouts when component unmounts
      Object.values(debounceRef.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
      debounceRef.current = {};
    };
  }, []);

  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      const newStartDate = dates[0];
      const newEndDate = dates[1];

      const daysDifference = moment(newEndDate).diff(moment(newStartDate), "days");
      if (daysDifference > 30) {
        message.error(
          "Date range cannot exceed 30 days. Please select a shorter date range."
        );
        return;
      }

      const newDateRange = [dates[0], dates[1]];

      setDateRange(newDateRange);

      // Reset pagination to first page when date changes
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));

      // Save state to session storage
      setTimeout(() => saveStateToSession(), 100);
    }
  };

  // Helper function to render status cards dynamically
  // const StatusCards = () => {
  //   const ordersCards = [
  //     {
  //       key: "total-orders",
  //       title: "Total New and In Progress Orders",
  //       value: totalOrdersCount,
  //       icon: require("../../assests/images/total-icon.png"),
  //       color: "#52c41a",
  //       isClickable: false,
  //     },
  //     {
  //       key: "total-manifested",
  //       title: "Total Orders Manifested",
  //       value: totalOrdersManifested,
  //       icon: require("../../assests/images/manifest-icon.png"),
  //       color: "#1890ff",
  //       isClickable: false,
  //     },
  //     {
  //       key: "total-prs",
  //       title: "Total Orders in PRS",
  //       value: totalOrdersInPRS,
  //       icon: require("../../assests/images/total-icon.png"),
  //       color: "#52c41a",
  //       isClickable: false,
  //     },
  //   ];

  //   // Add dynamic status cards from API response for PRS data
  //   const prsCards = statusCounts.map((status) => {
  //     return {
  //       ...status,
  //       key: status.prsStatus,
  //       title:
  //         status.label ||
  //         status.prsStatus.replace("PRS_", "").replace(/_/g, " "),
  //       value: status.count,
  //       icon: status.icon || AppstoreOutlined,
  //       color: status.color || "#666666",
  //       isClickable: true,
  //       prsStatus: status.prsStatus,
  //       onClick: () => {
  //         // Save current state before navigation
  //         saveStateToSession();
  //         navigate(`/prs-analytics/status/${status.prsStatus}`, {
  //           state: {
  //             dateRange: dateRange,
  //             columnFilters: columnFilters,
  //             pagination: pagination,
  //           },
  //         });
  //       },
  //     };
  //   });

  //   const renderCardRow = (cards, title) => (
  //     <div key={title} className="card-section mb-4">
  //       <div className="section-title mb-1">
  //         <h3
  //           style={{
  //             fontSize: "16px",
  //             fontWeight: "600",
  //             color: "#333",
  //             margin: 0,
  //           }}
  //         >
  //           {title}
  //         </h3>
  //       </div>
  //       <Row gutter={[12, 12]} className="summary-cards">
  //         {cards.map((card) => (
  //           <Col key={card.key} xs={24} sm={12} md={6} lg={4}>
  //             <Card
  //               size="small"
  //               className={
  //                 card.isClickable ? "status-card" : "status-card-default"
  //               }
  //               hoverable={card.isClickable}
  //               onClick={card.isClickable ? card.onClick : undefined}
  //               style={{ cursor: card.isClickable ? "pointer" : "default" }}
  //             >
  //               <Statistic
  //                 title={
  //                   <Space size="small">
  //                     {card.icon ? (
  //                       <img
  //                         src={card.icon}
  //                         alt={card.title}
  //                         className="status-card-icon"
  //                       />
  //                     ) : null}
  //                     <span
  //                       className="status-card-title"
  //                       style={{ color: card.color }}
  //                     >
  //                       {card.title}
  //                     </span>
  //                   </Space>
  //                 }
  //                 value={card.value}
  //                 valueStyle={{ color: card.color }}
  //                 className="status-card-value"
  //               />
  //             </Card>
  //           </Col>
  //         ))}
  //       </Row>
  //     </div>
  //   );

  //   return (
  //     <div className="status-cards-container">
  //       {renderCardRow(ordersCards, "Orders Summary")}
  //       {renderCardRow(prsCards, "Pickup Runsheet Summary")}
  //     </div>
  //   );
  // };

  // Helper function to create column filter
  const getColumnSearchProps = (dataIndex, placeholder) => {
    // Define minimum character requirements for different fields
    const getMinChars = (fieldName) => {
      if (
        fieldName === "vendorCode" ||
        fieldName === "pickupPincode" ||
        fieldName === "dpPincode"
      ) {
        return 2;
      }
      return 3;
    };

    const minChars = getMinChars(dataIndex);
    const placeholderText = `Search ${placeholder}...`;

    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={placeholderText}
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleColumnFilter(value, dataIndex);
            }}
            onPressEnter={() => {
              const value = selectedKeys[0] || "";
              handleSearch(dataIndex, value);
              confirm();
            }}
            style={{
              marginBottom: 8,
              display: "block",
              borderRadius: "6px",
            }}
            allowClear={false}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                const value = selectedKeys[0] || "";
                handleSearch(dataIndex, value);
                confirm();
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setSelectedKeys([]);
                handleSearch(dataIndex, "");
                confirm(); // Close the filter popup
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <div className={`filter-icon-container ${filtered ? "filtered" : ""}`}>
          <FilterOutlined
            className={`filter-icon ${filtered ? "filtered" : ""}`}
          />
        </div>
      ),
      filteredValue: columnFilters[dataIndex]
        ? [columnFilters[dataIndex]]
        : null,
    };
  };

  // Function to convert camelCase/snake_case to Title Case
  const generateTitle = (key) => {
    return key
      .replace(/([A-Z])/g, " $1") // Add space before capital letters
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/\b\w/g, (l) => l.toUpperCase()) // Capitalize first letter of each word
      .trim();
  };

  // Function to get column width based on field type
  const getColumnWidth = (key, title) => {
    // Date fields
    if (
      key.toLowerCase().includes("at") ||
      key.toLowerCase().includes("date")
    ) {
      return 160;
    }
    // Phone/Mobile fields
    if (
      key.toLowerCase().includes("phone") ||
      key.toLowerCase().includes("mobile")
    ) {
      return 150;
    }
    // Address fields
    if (key.toLowerCase().includes("address")) {
      return 100;
    }
    // ID fields
    if (key.toLowerCase().includes("id")) {
      return 130;
    }
    // Name fields
    if (key.toLowerCase().includes("name")) {
      return 200;
    }
    // Amount/Currency fields
    if (
      key.toLowerCase().includes("amount") ||
      key.toLowerCase().includes("currency")
    ) {
      return 100;
    }

    // Status fields
    if (
      key.toLowerCase().includes("status") ||
      key.toLowerCase().includes("number")
    ) {
      return 160;
    }

    // URL/Link fields
    if (
      key.toLowerCase().includes("url") ||
      key.toLowerCase().includes("link") ||
      key.toLowerCase().includes("website") ||
      key.toLowerCase().includes("domain")
    ) {
      return 200;
    }
    if (
      key.toLowerCase().includes("description") ||
      key.toLowerCase().includes("hub")
    ) {
      return 250;
    }
    // Default width
    return 100;
  };

  // Function to check if a value is a valid date string
  const isValidDateString = (value) => {
    if (!value || typeof value !== "string") return false;

    // Check if it's a valid ISO date string or other common date formats
    const date = moment(value);
    return (
      date.isValid() &&
      // Check for ISO date format (with T and Z)
      (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value) ||
        // Check for other common date formats
        /^\d{4}-\d{2}-\d{2}/.test(value))
    );
  };

  // Function to check if a value is a valid URL/link
  const isValidUrl = (value) => {
    if (!value || typeof value !== "string") return false;

    try {
      // Check for common URL patterns
      const urlPattern = /^(https?:\/\/)|(www\.)|([a-zA-Z0-9-]+\.[a-zA-Z]{2,})/;
      return (
        urlPattern.test(value) ||
        value.startsWith("http") ||
        value.startsWith("www")
      );
    } catch (error) {
      return false;
    }
  };

  // Function to get custom render function for specific fields
  const getCustomRender = (key) => {
    // Universal renderer - checks actual value for dates and links
    const universalRenderer = (text) => {
      // if (!text) return "-";

      // Check if it's actually a valid date string
      if (isValidDateString(text)) {
        return moment(text).format("DD-MM-YYYY hh:mm A");
      }

      // Check if it's a valid URL and render as clickable link
      if (isValidUrl(text)) {
        const LinkRenderer = () => {
          // Ensure URL has protocol for proper linking
          const url = text.startsWith("http") ? text : `https://${text}`;

          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#1890ff",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation(); // Prevent row click events
              }}
            >
              {text}
            </a>
          );
        };
        LinkRenderer.displayName = "LinkRenderer";
        return <LinkRenderer />;
      }

      // If not a date or link, return as is
      return text;
    };

    // Apply universal rendering for fields that might contain dates or links
    if (
      key.toLowerCase().includes("at") ||
      key.toLowerCase().includes("date") ||
      key.toLowerCase().includes("url") ||
      key.toLowerCase().includes("link") ||
      key.toLowerCase().includes("website") ||
      key.toLowerCase().includes("domain")
    ) {
      return universalRenderer;
    }

    // Status fields with underscores - handle different status types
    if (key.toLowerCase().includes("status")) {
      const StatusRenderer = (status) => {
        // if (!status) return "-";

        // For payment status, just show as is with proper formatting
        if (key.toLowerCase().includes("payment")) {
          return (
            <span className="status-value-bold">
              {status.replace(/_/g, " ")}
            </span>
          );
        }

        // For other status fields, remove PRS_ prefix and format
        return (
          <span className="status-value-bold">
            {status.replace(/PRS_/g, "").replace(/_/g, " ")}
          </span>
        );
      };
      StatusRenderer.displayName = "StatusRenderer";
      return StatusRenderer;
    }

    // PRS Number with navigation
    if (key === "prsNumber") {
      const PRSNumberRenderer = (text, record) => {
        if (text === "NA" || !text) {
          return <span style={{ color: "#666" }}>NA</span>;
        }
        return (
          <a
            onClick={() => {
              saveStateToSession();
              navigate(`/prs-details/${text}`);
            }}
            style={{ cursor: "pointer" }}
          >
            {text}
          </a>
        );
      };
      PRSNumberRenderer.displayName = "PRSNumberRenderer";
      return PRSNumberRenderer;
    }

    const DynamicFallbackRenderer = (value) => {
      // Dynamic Boolean Check
      if (typeof value === "boolean") {
        const BooleanRenderer = () => <span>{String(value)}</span>;
        BooleanRenderer.displayName = "DynamicBooleanRenderer";
        return <BooleanRenderer />;
      }

      // If it's not a boolean and not "NA", return the value as is.
      return value;
    };
    DynamicFallbackRenderer.displayName = "DynamicFallbackRenderer";

    return DynamicFallbackRenderer; // <-- Return the named function
  };

  // Dynamic column generation based on data and searchable fields
  const generateColumns = (dataSource, searchableFieldsArray = []) => {
    if (!dataSource || dataSource.length === 0) {
      return [];
    }

    const sampleRecord = dataSource[0];
    const keys = Object.keys(sampleRecord);

    return keys.map((key) => {
      const title = generateTitle(key);
      const width = getColumnWidth(key, title);
      const customRender = getCustomRender(key);

      const baseColumn = {
        title,
        dataIndex: key,
        key,
        ...(width && { width }),
        ...(customRender && { render: customRender }),
      };

      // Add search functionality dynamically based on searchable fields from API
      const isSearchableField = searchableFieldsArray.includes(key);
      if (isSearchableField) {
        return {
          ...baseColumn,
          ...getColumnSearchProps(key, title),
        };
      }

      // Add ellipsis for address and long text fields
      if (
        key.toLowerCase().includes("address") ||
        key.toLowerCase().includes("name")
      ) {
        baseColumn.ellipsis = true;
      }

      return baseColumn;
    });
  };

  // Get dynamic columns with searchable fields
  const columns = generateColumns(data, searchableFields);

  const activeFiltersCount = Object.values(columnFilters).filter(
    (value) => value && value.trim() !== ""
  ).length;

  return (
    <div className="prs-analytics-container">
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" autoComplete="off">
          <div className="prs-analytics-heading">ECOMM Analytics Dashboard</div>

          <Card className="prs-analytics-card">
            {/* Date Range and Action Buttons Row */}
            <Row gutter={[8, 24]} align="bottom" className="mb-4">
              <Col xs={24} sm={12} md={14} lg={5}>
                <Form.Item
                  label={
                    <Space size="small">
                      <span>Order Created Date Range</span>
                    </Space>
                  }
                  required
                  tooltip={{
                    title:
                      "This date range filters orders by their last updated date. All analytics, status counts, and data will be shown for orders updated within this period.",
                    placement: "topLeft",
                  }}
                  style={{ marginBottom: 0 }}
                >
                  <RangePicker
                    className="input-field-style date-range-picker"
                    value={dateRange}
                    format={dateFormat_dmy}
                    onChange={handleDateRangeChange}
                    allowClear={false}
                    disabledDate={(current) =>
                      current && current > moment().endOf("day")
                    }
                    placeholder={["Start Date", "End Date"]}
                    separator="→"
                  />
                </Form.Item>
              </Col>
              <Col>
                <div className="action-buttons-container">
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      const currentPage = pagination.current || 1;
                      const currentPageSize = pagination.pageSize || 10;
                      fetchAllData(currentPage, currentPageSize, columnFilters);
                    }}
                  >
                    Refresh Data
                  </Button>
                </div>
              </Col>
            </Row>

            {/* Summary Cards Section with Collapsible Header */}
            {/* <div className="summary-section">
              <div
                className="summary-header"
                onClick={() => setCardsCollapsed(!cardsCollapsed)}
              >
                <div className="summary-header-content">
                  <h3 className="summary-title">
                    <AppstoreOutlined className="summary-icon" />
                    Analytics Overview
                  </h3>
                  <span className="summary-subtitle">
                    {cardsCollapsed ? "Click to view" : "Click to collapse"}{" "}
                    Orders and PRS status summary
                  </span>
                </div>
                <Button
                  type="text"
                  icon={cardsCollapsed ? <DownOutlined /> : <UpOutlined />}
                  className="collapse-button"
                  size="small"
                />
              </div>

              {!cardsCollapsed && (
                <div className="summary-content">
                  <StatusCards />
                </div>
              )}
            </div> */}

            {/* Detailed Report Section */}
            <div className="report-section">
              <Row gutter={[24, 24]}>
                <Col span={24}>
                  <div className="table-title-section">
                    <div className="title-with-actions">
                      <div className="title-content">
                        {/* <h3 className="table-title">Report</h3> */}
                      </div>
                      <div className="title-actions">
                        <Button
                          type="primary"
                          icon={<DownloadOutlined />}
                          onClick={handleExport}
                          className="mr-2"
                        >
                          Export Data
                        </Button>
                        <Button
                          type="default"
                          icon={<ClearOutlined />}
                          onClick={handleClearFilters}
                          disabled={activeFiltersCount === 0}
                        >
                          Clear Filters{" "}
                          {activeFiltersCount > 0 && `(${activeFiltersCount})`}
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="table-container">
                    <Table
                      columns={columns}
                      dataSource={data}
                      rowKey={(record) =>
                        `${record.awbNumber}-${record.updatedDate}`
                      }
                      pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                          `${range[0]}-${range[1]} of ${pagination.total} items`,
                        showQuickJumper: true,
                        pageSizeOptions: ["50", "100", "200", "500"],
                        position: ["bottomRight"],
                      }}
                      onChange={handleTableChange}
                      scroll={{ x: "max-content" }}
                      loading={loading}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </Card>
        </Form>
      </Spin>
    </div>
  );
}

export default PRSAnalytics;
